import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { CustomPipe } from '../../../shared/pipes/custom.pipe';
import { UsersService } from '../../../shared/services/users/users.service';
import { filter, Subscription } from 'rxjs';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';

@Component({
  selector: 'app-prices',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    TitleComponent,
    LoaderComponent,
    CustomPipe,
    RouterOutlet,
    QeqPipe,
  ],
  templateUrl: './prices.component.html',
  styleUrls: ['./prices.component.css', '../../../shared/styles/layout.css'],
})
export class PricesComponent implements OnDestroy {
  title: string = 'Precios';
  subtitle: string = 'Gestiona los precios del comedor.';
  isUuid: boolean = false;

  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];
  private navigationSubscription: Subscription | null = null;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private restaurantService: RestaurantService,
    private storageService: StorageService
  ) {}

  get isLoading() {
    return this.loaderService.getLoader();
  }

  readonly dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);

  displayedColumns: string[] = [
    'price',
    'price_company',
    'type',
    'author',
    'id',
    'start',
    'finish',
    'acciones',
  ];

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  // Main paginator (for active prices)
  @ViewChild('activePaginator') paginator!: MatPaginator;

  // Add a second paginator for historical prices
  @ViewChild('historicalPaginator') historicalPaginator!: MatPaginator;

  // Create separate data sources for active and historical prices
  activeDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  historicalDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    // Set up sorting for both data sources
    this.activeDataSource.sort = this.sort;
    this.historicalDataSource.sort = this.sort;

    // Bind paginators to respective data sources
    this.activeDataSource.paginator = this.paginator;
    this.historicalDataSource.paginator = this.historicalPaginator;

    this.setupCustomSorting();
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  deleteSelected(id: number, nick: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar este precio?',
        message: nick,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          return this.restaurantService.deleteRestaurantPrice(id);
        },
      },
    });

    // Esperar a que el diálogo se cierre y actualizar las tablas
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        // Update all data sources
        const filteredData = this.dataSource.data.filter(
          (price) => price.id !== id
        );
        this.dataSource.data = filteredData;

        // Update active prices if the deleted price was active
        this.activePrices = this.activePrices.filter(
          (price) => price.id !== id
        );
        this.activeDataSource.data = this.activePrices;

        // Update historical prices if the deleted price was historical
        this.historicalPrices = this.historicalPrices.filter(
          (price) => price.id !== id
        );
        this.historicalDataSource.data = this.historicalPrices;

        // Show success message
        this._snackBar.open('Precio eliminado con éxito', 'Cerrar', {
          duration: 3000,
        });
      }
    });
  }

  getPrices() {
    return this.restaurantService.getRestaurantPrices();
  }

  myroles: any = {}; // Change from array to object

  // Add these two arrays
  activePrices: any[] = [];
  historicalPrices: any[] = [];

  ngOnInit() {
    this.loaderService.showLoader();

    // Clear any existing navigation subscription before creating a new one
    this.setupNavigationListener();

    // Get roles
    this.loadRoles();

    // Only load price data if we're on the main prices page
    if (!this.isUuid) {
      this.loadPriceData();
    }
  }

  setupNavigationListener() {
    // Clean up existing navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Set up new navigation subscription
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const isEditOrAdd =
          this.router.url.includes('/dining/prices/edit') ||
          this.router.url.includes('/dining/prices/add');

        if (isEditOrAdd) {
          this.isUuid = true;
        } else if (this.router.url.includes('/dining/prices')) {
          this.isUuid = false;

          // Clear existing subscriptions before reloading data
          this.clearSubscriptions();

          // Reload data only when we're on the main prices page
          this.loadRoles();
          this.loadPriceData();
        }

        this.cdr.detectChanges();
      });
  }

  loadRoles() {
    // Use the refactored method in RolesService
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'prices',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      //  console.log(
      //    'No user roles found in storage or no permissions for Comedor'
      //  );
    }
  }

  loadPriceData() {
    const pricesSub = this.getPrices().subscribe({
      next: (prices) => {
        // console.log('Fetched prices:', prices);

        // No need to fetch usernames, just use the data as is
        let pricesWithUsernames = [...prices];

        // Get current date for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        //  console.log('Today for comparison:', today);

        // Split into active and historical based on date ranges
        this.activePrices = pricesWithUsernames.filter((p) => {
          // If no end date, it's always active (indefinite)
          if (p.valid_until === null) {
            //   console.log(`Price ID ${p.id} (no end date): Always active`);
            return true;
          }

          // If has end date, it's active if the end date hasn't passed yet
          const validUntil = new Date(p.valid_until);
          validUntil.setHours(23, 59, 59, 999); // Set to end of day

          const isActive = today <= validUntil;
          //   console.log(
          //   `Price ID ${p.id} (with end date): validUntil=${validUntil}, isActive=${isActive}`
          //  );
          return isActive;
        });

        this.historicalPrices = pricesWithUsernames.filter((p) => {
          // If no end date, it's never historical
          if (p.valid_until === null) {
            //  console.log(`Price ID ${p.id} (no end date): Never historical`);
            return false;
          }

          // If has end date, it's historical only if the end date has passed
          const validUntil = new Date(p.valid_until);
          validUntil.setHours(23, 59, 59, 999);

          const isHistorical = today > validUntil;
          //   console.log(
          //    `Price ID ${p.id} (with end date): validUntil=${validUntil}, isHistorical=${isHistorical}`
          //   );
          return isHistorical;
        });

        //  console.log('Active prices:', this.activePrices);
        //  console.log('Historical prices:', this.historicalPrices);

        // Update both data sources
        this.activeDataSource.data = this.activePrices;
        this.historicalDataSource.data = this.historicalPrices;

        // Keep original dataSource for compatibility
        this.dataSource.data = pricesWithUsernames;

        // Apply paginators
        setTimeout(() => {
          if (this.paginator) this.activeDataSource.paginator = this.paginator;
          if (this.historicalPaginator)
            this.historicalDataSource.paginator = this.historicalPaginator;
          if (this.sort) {
            this.activeDataSource.sort = this.sort;
            this.historicalDataSource.sort = this.sort;
          }
        });

        this.setupCustomSorting();
        this.loaderService.hideLoader();
      },
      error: (err) => {
        console.error(err);
        this.loaderService.hideLoader();
      },
    });

    this.subscriptions.push(pricesSub);
  }

  // Modify setupCustomSorting to apply to both data sources
  private setupCustomSorting() {
    const sortingAccessor = (item: any, property: string) => {
      switch (property) {
        case 'price':
          return parseFloat(item.price);
        case 'price_company':
          return parseFloat(item.price_company);
        case 'type':
          return item.meal_type;
        case 'start':
          return new Date(item.valid_from).getTime();
        case 'finish':
          return item.valid_until
            ? new Date(item.valid_until).getTime()
            : Number.MAX_SAFE_INTEGER;
        default:
          return item[property];
      }
    };

    this.dataSource.sortingDataAccessor = sortingAccessor;
    this.activeDataSource.sortingDataAccessor = sortingAccessor;
    this.historicalDataSource.sortingDataAccessor = sortingAccessor;
  }

  clearSubscriptions() {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });

    // Reset the subscriptions array
    this.subscriptions = [];
  }

  ngOnDestroy() {
    // Clean up all subscriptions when component is destroyed
    this.clearSubscriptions();

    // Also clean up navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
  }

  goToEdit(id: number) {
    this.router.navigate(['/home/dining/prices/edit', id]);
  }

  addPrice() {
    // Check if user has write permission before navigating
    const hasWritePermission = this.rolesService.checkWritePermission(
      'Comedor',
      this.storageService,
      this._snackBar,
      this.router,
      '/home/dining/prices',
      'No tienes permisos para añadir precios'
    );

    if (hasWritePermission) {
      this.router.navigate(['/home/dining/prices/add']);
    }
  }
}
